import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, EMPTY } from 'rxjs';
import { map, shareReplay, filter, tap, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import Parse from 'parse';

import { RoiStore, MicroLayoutStore, AssetStore, AnalystStore } from '../../stores';
import { RoiLabelingEffects } from '../roi-labeling';

@Injectable()
export class MicroContextMenuEffects {
  private readonly _contextMenuVisible$ = new BehaviorSubject<boolean>(false);
  private readonly _contextMenuPosition$ = new BehaviorSubject<{ x: number; y: number }>({ x: 0, y: 0 });

  public readonly contextMenuVisible$ = this._contextMenuVisible$.asObservable();
  public readonly contextMenuPosition$ = this._contextMenuPosition$.asObservable();

  // --- Computed selectors ---

  public readonly selectedRois$ = combineLatest([
    this.microLayout.selectedRois$,
    this.roiStore.rois$,
    this.assetStore.selectedAsset$,
  ]).pipe(
    map(([selectedRois, rois, selectedAsset]) => {
      const assetId = selectedAsset?.id;
      const filtered = new Set<string>();
      selectedRois.forEach((roiId) => {
        const roi = rois[roiId];
        if (roi && roi.labels.size > 0 && (!assetId || roi.assetId === assetId)) {
          filtered.add(roiId);
        }
      });
      return filtered;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly canEdit$ = combineLatest([
    this.analystStore.selectedAnalyst$,
    this.microLayout.editingEnabled$,
  ]).pipe(
    map(([selectedAnalyst, editingEnabled]) => {
      const isReviewingOtherAnalyst = selectedAnalyst ? selectedAnalyst.id !== Parse.User.current()?.id : false;
      return !isReviewingOtherAnalyst || editingEnabled;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly selectedItemLabels$ = combineLatest([this.selectedRois$, this.roiStore.rois$]).pipe(
    map(([selectedRois, rois]) => {
      const labels: string[] = [];
      selectedRois.forEach((roiId) => {
        const roi = rois[roiId];
        if (roi?.labels) {
          Array.from(roi.labels.keys()).forEach((labelId) => {
            if (!labels.includes(labelId)) labels.push(labelId);
          });
        }
      });
      return labels;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly doubleCheckedOptions$ = combineLatest([
    this.selectedRois$,
    this.roiStore.rois$,
    this.selectedItemLabels$,
  ]).pipe(
    map(([selectedRois, rois, selectedItemLabels]) => {
      if (selectedRois.size === 0 || selectedItemLabels.length === 0) return [];
      return selectedItemLabels.filter((labelId) =>
        Array.from(selectedRois).every((roiId) => rois[roiId]?.labels?.has(labelId))
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly labelsInAllSelectedRois$ = combineLatest([this.selectedRois$, this.roiStore.rois$]).pipe(
    map(([selectedRois, rois]) => {
      if (selectedRois.size === 0) return new Set<string>();
      const roiArray = Array.from(selectedRois)
        .map((roiId) => rois?.[roiId])
        .filter((roi): roi is NonNullable<typeof roi> => roi !== undefined && roi.labels.size > 0);
      if (roiArray.length === 0) return new Set<string>();
      let commonLabels = new Set(roiArray[0].labels.keys());
      for (let i = 1; i < roiArray.length; i++) {
        const currentLabels = new Set(roiArray[i].labels.keys());
        commonLabels = new Set([...commonLabels].filter((label) => currentLabels.has(label)));
      }
      return commonLabels;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly labelsInSomeSelectedRois$ = combineLatest([this.selectedRois$, this.roiStore.rois$]).pipe(
    map(([selectedRois, rois]) => {
      if (selectedRois.size === 0) return new Set<string>();
      const allLabelsSet = new Set<string>();
      for (const roiId of selectedRois) {
        const roi = rois?.[roiId];
        if (roi) {
          for (const labelId of roi.labels.keys()) allLabelsSet.add(labelId);
        }
      }
      return allLabelsSet;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiLabelingEffects: RoiLabelingEffects,
    private readonly microLayout: MicroLayoutStore,
    private readonly assetStore: AssetStore,
    private readonly analystStore: AnalystStore
  ) {
    this.autoOpenProtocolPanelEffect(this.selectedRois$);
  }

  // --- Effects ---

  public readonly showContextMenu = this.roiStore.effect((event$: Observable<MouseEvent>) =>
    event$.pipe(
      concatLatestFrom(() => [this.selectedRois$, this.canEdit$]),
      filter(([, selectedRois, canEdit]) => selectedRois.size > 0 && canEdit),
      tap(([event]) => {
        this._contextMenuPosition$.next({ x: event.clientX, y: event.clientY });
        this._contextMenuVisible$.next(true);
      }),
      switchMap(() => EMPTY)
    )
  );

  public readonly closeContextMenu = this.roiStore.effect((action$: Observable<void>) =>
    action$.pipe(
      tap(() => this._contextMenuVisible$.next(false)),
      switchMap(() => EMPTY)
    )
  );

  public readonly onContextMenuOptionSelected = this.roiStore.effect((labelId$: Observable<string>) =>
    labelId$.pipe(
      concatLatestFrom(() => this.selectedRois$),
      tap(([labelId, selectedRois]) => {
        this.roiLabelingEffects.addLabels({ roiIds: Array.from(selectedRois), labels: [labelId] });
      }),
      switchMap(() => EMPTY)
    )
  );

  public readonly onContextMenuOptionDeselected = this.roiStore.effect((labelId$: Observable<string>) =>
    labelId$.pipe(
      concatLatestFrom(() => this.selectedRois$),
      filter(([, selectedRois]) => selectedRois.size > 0),
      tap(([labelId, selectedRois]) => {
        this.roiLabelingEffects.removeLabels({ roiIds: Array.from(selectedRois), labels: [labelId] });
      }),
      switchMap(() => EMPTY)
    )
  );

  private readonly autoOpenProtocolPanelEffect = this.microLayout.effect((selectedRois$: Observable<Set<string>>) =>
    selectedRois$.pipe(
      distinctUntilChanged((prev, curr) => prev.size === curr.size),
      filter((selectedRois) => selectedRois.size > 0),
      tap(() => {
        this.microLayout.setProtocolPanelCollapsed(false);
        this.microLayout.setMultiSelection(true);
      }),
      switchMap(() => EMPTY)
    )
  );
}
